/**
 * client/src/cma/CompCards.tsx — the comparable sales as a set of properties, not a spreadsheet.
 *
 * Owner, 2026-08-29: *"the comparison page where we can select comps and look at sales data and
 * dates … looks archaic. We need to make it look more modern. It needs to be smooth … more website
 * like and less excel looking."*
 *
 * WHAT THE TABLE GETS WRONG FOR THIS PARTICULAR JOB. Choosing comparables is not data entry; it is
 * a judgement about houses. Eighteen columns give every attribute the same weight — the pool flag
 * is the same size as the sold price — and the one question the agent is actually answering, *is
 * this sale like my subject*, has to be reconstructed by reading sideways across a row and holding
 * the subject in your head. The grid is the right tool for checking and correcting a figure. It is
 * the wrong one for deciding.
 *
 * SO THIS IS A SECOND VIEW, NOT A REPLACEMENT. The table is still there, one click away, and it is
 * still where a figure gets corrected — `ConfirmGrid` remains the complete surface. What the cards
 * add is a reading order: identity, the three figures that decide it, how it differs from the
 * subject, and what the sale is still short of.
 *
 * EVERY FIGURE STILL RESOLVES TO A SOURCE. That is the whole point of the product, and it is the
 * thing a prettier surface is most likely to quietly drop. Each headline figure is a `Field` built
 * by `compFigures.ts` and rendered through `ProvenanceChip` — the same citation component the
 * result screen uses — so clicking any of the three opens the drawer that names its source. Price
 * per square foot, which no record states, carries the SOURCE tag and a `computed` STATE tag beside
 * it and shows its own division in the drawer.
 *
 * THE DIFFERENCES ARE NOT ADJUSTMENTS. "38 SF larger" is a subtraction of two numbers both on the
 * screen. Nothing here is a dollar figure and nothing here reaches the engine; `engine.amb` does the
 * adjusting, from the same source figures, on its own terms. A difference is omitted rather than
 * guessed when either side is missing — an unknown living area does not make a sale "the same size".
 */
import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";

import { themeVar as colors } from "../design-tokens";
import { ProvenanceChip } from "../components/ProvenanceChip";
import { CompNumInput } from "./CompNumInput";
import { isProviderRow } from "./cascade";
import { COMP_REQUIRED_FIELDS, compMissingFields } from "./ReadinessGate";
import { cumulativeDaysOnMarketLabel, daysOnMarketLabel } from "./daysOnMarket";
import {
  bedBathLine, deltasVsSubject, headlineFigures, money, shortDate, sourceOf, sqft,
} from "./compFigures";
import { combinedSiteLine } from "./compCriteria";
import type { CandidateComp, SubjectGrid } from "./types";
import {
  effectivePlaces, leadOf, LEAD_SHORT, LEAD_WORDS, moveLead, placedIndices, reachablePlaces, stepRefusal,
  type LeadPosition,
} from "./compLadder";

export function CompCards({
  comps,
  order,
  subjectGrid,
  rungLabels,
  onToggle,
  onEdit,
  onSwitchToTable,
  onLead,
  onStep,
  onMove,
  activeIndex = null,
  onActiveChange,
  showPinNumbers = false,
}: {
  /** Rank editing on the card (comps-ux): the agent picks a place for a ticked sale. Absent = the
   * card shows the place and says where to change it. */
  onLead?: (index: number, position: LeadPosition | null) => void;
  /** Click-to-step (2026-11-03): move one ticked sale a place up (-1) or down (+1). The neighbour it
   * passes takes its place, so the numbers stay 1..n and unique. Absent = the card shows no
   * step buttons and the select above is the only way to change a place. */
  onStep?: (index: number, direction: -1 | 1) => void;
  /** Click the number: move one ticked sale straight to a place (1..n); the sales between shift to
   * keep the places contiguous. Same ladder as `onStep`. */
  onMove?: (index: number, place: number) => void;
  /** 1G — the map link. A card's number is its position in `order` plus one: the same number its
   * pin carries. Hovering or focusing a card calls `onActiveChange(index)`; leaving calls it with
   * null. `activeIndex` is the one the map or another card is pointing at. */
  activeIndex?: number | null;
  onActiveChange?: (index: number | null) => void;
  showPinNumbers?: boolean;
  comps: CandidateComp[];
  /** The indices to draw, in the order the table would draw them — one sort, two views. */
  order: number[];
  subjectGrid?: SubjectGrid | null;
  rungLabels?: Record<string, string>;
  onToggle: (index: number, next: boolean) => void;
  onEdit: (index: number, patch: Partial<CandidateComp>) => void;
  /** Correcting a figure happens in one place. The card offers the way there rather than a copy. */
  onSwitchToTable: () => void;
}): ReactElement {
  // TYPE NOT STATED (2026-10-22): a sale whose record states no property type, when the search
  // chose types, is listed after the matches under its own heading. Never hidden, never counted as
  // a match. Its pin number is unchanged (the map numbers by `order`), and `order` itself is not
  // touched, so the printed order and the recorded order cannot drift.
  const places = effectivePlaces(comps);
  const reachable = reachablePlaces(comps);
  // After a step the sale may sit somewhere else in the list (the ladder leads it), and a moved DOM
  // node can drop keyboard focus. Put focus back on the button that was pressed once the new order
  // is drawn, and say in words what moved, for a screen reader that cannot see the cards swap.
  const [announce, setAnnounce] = useState("");
  const [refocus, setRefocus] = useState<{ index: number; target: "up" | "down" | "num" } | null>(null);
  useEffect(() => {
    if (!refocus) return;
    const sel = `[data-comp-index="${refocus.index}"] [data-step="${refocus.target}"]`;
    document.querySelector<HTMLElement>(sel)?.focus();
    setRefocus(null);
  }, [refocus, comps]);
  const step = (i: number, direction: -1 | 1) => {
    if (!onStep) return;
    const placed = placedIndices(comps);
    const at = placed.indexOf(i);
    const other = placed[at + direction];
    if (at >= 0 && other !== undefined) {
      setAnnounce(`${comps[i]?.name ?? "This sale"} moved to comp ${at + 1 + direction}. ${comps[other]?.name ?? "The other sale"} is now comp ${at + 1}.`);
    }
    onStep(i, direction);
    setRefocus({ index: i, target: direction < 0 ? "up" : "down" });
  };
  const move = (i: number, place: number) => {
    if (!onMove) return;
    const after = placedIndices(moveLead(comps, i, place));
    if (after.indexOf(i) < 0 || after.indexOf(i) + 1 !== place) return;
    const others = after.filter((j) => j !== i)
      .map((j) => `${comps[j]?.name ?? "A sale"} is now comp ${after.indexOf(j) + 1}`).join(", ");
    setAnnounce(`${comps[i]?.name ?? "This sale"} moved to comp ${place}.${others ? ` ${others}.` : ""}`);
    onMove(i, place);
    setRefocus({ index: i, target: "num" });
  };
  const stated = order.filter((i) => !comps[i]?._home_type_unstated);
  const unstated = order.filter((i) => comps[i]?._home_type_unstated);
  const card = (i: number) => (
    <CompCard
      key={`${comps[i]?.name ?? "comp"}-${i}`}
      comp={comps[i]}
      index={i}
      pinNumber={showPinNumbers ? order.indexOf(i) + 1 : null}
      active={activeIndex === i}
      onActiveChange={onActiveChange}
      subjectGrid={subjectGrid}
      rungLabels={rungLabels}
      onToggle={onToggle}
      onEdit={onEdit}
      onSwitchToTable={onSwitchToTable}
      place={places.get(i) ?? null}
      reachable={reachable}
      onLead={onLead}
      onStep={onStep ? step : undefined}
      onMove={onMove ? move : undefined}
      placeCount={placedIndices(comps).length}
      upWhy={onStep ? stepRefusal(comps, i, -1) : null}
      downWhy={onStep ? stepRefusal(comps, i, 1) : null}
    />
  );
  return (
    <div className="vb-comp-cards">
      <p className="vb-sr-only" role="status" aria-live="polite">{announce}</p>
      {stated.map(card)}
      {unstated.length > 0 && (
        <section className="vb-comp-group" aria-label="Property type not stated">
          <h4 className="vb-comp-group-h">Property type not stated</h4>
          <p className="vb-comp-group-note">
            {unstated.length === 1 ? "This sale's record doesn't" : `These ${unstated.length} sales' records don't`} say
            whether {unstated.length === 1 ? "it's" : "they're"} a single-family home, a townhome or a condo, so {unstated.length === 1 ? "it isn't" : "they aren't"} counted
            as {unstated.length === 1 ? "a match" : "matches"}. You can still include {unstated.length === 1 ? "it" : "them"}.
          </p>
        </section>
      )}
      {unstated.map(card)}
    </div>
  );
}

function CompCard({
  comp, index, subjectGrid, rungLabels, onToggle, onEdit, onSwitchToTable,
  pinNumber = null, active = false, onActiveChange, place = null, reachable = [], onLead,
  onStep, onMove, placeCount = 0, upWhy = null, downWhy = null,
}: {
  onMove?: (index: number, place: number) => void;
  /** How many places exist for the ticked set (1..n). */
  placeCount?: number;
  onStep?: (index: number, direction: -1 | 1) => void;
  /** Why the up / down button cannot act, or null when it can. */
  upWhy?: string | null;
  downWhy?: string | null;
  place?: LeadPosition | null;
  reachable?: LeadPosition[];
  onLead?: (index: number, position: LeadPosition | null) => void;
  comp: CandidateComp;
  index: number;
  pinNumber?: number | null;
  active?: boolean;
  onActiveChange?: (index: number | null) => void;
  subjectGrid?: SubjectGrid | null;
  rungLabels?: Record<string, string>;
  onToggle: (index: number, next: boolean) => void;
  onEdit: (index: number, patch: Partial<CandidateComp>) => void;
  onSwitchToTable: () => void;
}): ReactElement {
  const [openFields, setOpenFields] = useState(false);

  const missing = compMissingFields(comp as unknown as Record<string, unknown>);
  const blocked = missing.length > 0;
  const selected = !!comp._selected;
  const provider = isProviderRow(comp, rungLabels);
  const label = (k: string) => COMP_REQUIRED_FIELDS.find((f) => f.key === k)?.label ?? k;
  const figures = headlineFigures(comp, index);
  const deltas = deltasVsSubject(comp, subjectGrid);
  const name = comp.name ?? "This sale";

  return (
    <article
      className="vb-comp-card"
      data-selected={selected || undefined}
      data-blocked={blocked || undefined}
      data-comp-index={index}
      data-active={active || undefined}
      data-outside={comp._outside_criteria || undefined}
      onMouseEnter={onActiveChange ? () => onActiveChange(index) : undefined}
      onMouseLeave={onActiveChange ? () => onActiveChange(null) : undefined}
      onFocus={onActiveChange ? () => onActiveChange(index) : undefined}
      onBlur={onActiveChange ? () => onActiveChange(null) : undefined}
    >
      <header className="vb-comp-head">
        {pinNumber !== null && (
          <span className="vb-comp-pin" data-selected={selected || undefined}
                title={`Pin ${pinNumber} on the map`}>
            <span className="vb-sr-only">Map pin </span>{pinNumber}
          </span>
        )}
        {/*
          A REAL CHECKBOX, WEARING A BETTER COAT. The control is the same `<input type="checkbox">`
          the table has, with the same `Include <name>` label — so the keyboard, the screen reader
          and every existing assertion about this screen keep working. Only its paint changed.
        */}
        <label className="vb-comp-pick" title={blocked
          ? `Enter this sale's ${missing.map(label).join(", ")} to include it`
          : selected ? "Included in the report" : "Include this sale"}>
          <input
            type="checkbox"
            checked={selected}
            disabled={blocked}
            aria-label={`Include ${name}`}
            onChange={(e) => onToggle(index, e.target.checked)}
          />
          <span className="vb-comp-tick" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path d="M3 8.5l3 3 7-7" stroke="currentColor" strokeWidth="2.2"
                    strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </label>

        <div className="vb-comp-id">
          <h4 className="vb-comp-name">{name}</h4>
          <p className="vb-comp-where">
            {[comp.city, comp.state].filter(Boolean).join(", ") || null}
            {comp.subdivision && <span className="vb-comp-hood">{comp.subdivision}</span>}
            {comp.mls_id && <span className="vb-comp-mls">MLS #{comp.mls_id}</span>}
          </p>
          {/* 1G: under the address, not in the header row, so a long street name never gets squeezed
              to one letter per line at phone width (UX review, measured at 390). */}
          {comp._outside_criteria && (
            <p className="vb-comp-outside">Outside your current search. You can still include it.</p>
          )}
        </div>

        {/* Distance is display-only wherever it appears: a typed distance would be a claim about
            geography, and geography is not an opinion. Absent on an uploaded row, and honestly so. */}
        {/* WHERE THIS SALE STANDS (2026-09-08). SHOWN HERE, SET IN THE TABLE — the same rule the
            card already follows for correcting a figure: one control, one place, and the card
            offers the way there rather than a second copy of it. A chip that could be changed on
            two surfaces is two places for the ladder's contiguity rule to drift. */}
        {place !== null && (
          <span className="vb-comp-lead-chip"
                title={leadOf(comp) !== null ? "The order you set" : "Where the order listed puts this sale"}>
            {LEAD_WORDS[place]}
          </span>
        )}
        {typeof comp.distance === "number" && Number.isFinite(comp.distance) && (
          <span className="vb-comp-dist">{comp.distance.toFixed(2)} mi</span>
        )}
      </header>

      <div className="vb-comp-figures">
        {figures.map((f) => (
          <div className="vb-comp-figure" key={f.id}>
            {/* LABEL FIRST. The three figures carry a different number of tags — price per square
                foot has two, the others one — so a label underneath sits at three different heights
                across one card. Above the number it gives the row a single baseline to start from.
                Short enough not to wrap in a third of a card; `Field.label` stays the full name,
                which is what the drawer and the screen reader announce. */}
            <p className="vb-comp-figure-label">{SHORT_LABEL[f.id.split("-").pop() ?? ""] ?? f.label}</p>
            <ProvenanceChip field={f} size="figure" />
          </div>
        ))}
      </div>

      <p className="vb-comp-facts">
        <span>Sold {shortDate(comp.sale_date)}</span>
        {/* THE RULE, NOT THE RAW FIELD. `daysOnMarketLabel` mirrors `engine.amb`'s own
            current-days-on-market and says "(n when read)" when the count has grown since the file
            was read. A card that printed `dom` would disagree with the report on the same sale. */}
        {daysOnMarketLabel(comp, comp.dom_as_of) !== "—" && (
          <span>{daysOnMarketLabel(comp, comp.dom_as_of)} days on market</span>
        )}
        {cumulativeDaysOnMarketLabel(comp) && <span>{cumulativeDaysOnMarketLabel(comp)}</span>}
        {comp.year != null && Number.isFinite(comp.year) && <span>Built {comp.year}</span>}
        {bedBathLine(comp) && <span>{bedBathLine(comp)}</span>}
        {comp.lot_sf != null && Number.isFinite(comp.lot_sf) && <span>{sqft(comp.lot_sf)} lot</span>}
        {lotProvenance(comp) && <span data-testid="comp-lot-source">{lotProvenance(comp)}</span>}
        {combinedSiteLine(comp) && (
          <span data-testid="comp-site-combined">
            {combinedSiteLine(comp)!.acres && <>{combinedSiteLine(comp)!.acres} combined <span className="vb-tag">{combinedSiteLine(comp)!.tag}</span>{" "}</>}
            <span data-testid="comp-site-how">{combinedSiteLine(comp)!.how}</span>
          </span>
        )}
        {comp._site_unlisted && <span data-testid="comp-site-unlisted">site size not stated</span>}
        {comp.pool && <span>Pool</span>}
      </p>

      {(comp._quality_flags ?? []).length > 0 && (
        <ul className="vb-comp-quality" aria-label="Data-quality flags">
          {(comp._quality_flags ?? []).map((f) => (
            <li key={f.code} data-severity={f.severity} className="vb-comp-quality-flag">
              <span>{f.message}</span>{" "}
              {f.rule && <span className="vb-comp-quality-rule">{f.rule}</span>}{" "}
              {f.computed && (
                <span className="vb-comp-quality-computed" data-testid="comp-quality-computed">
                  {f.computed.label}: {f.computed.value}{f.computed.unit}{" "}
                  <span className="vb-comp-deltas-tag">{f.computed.tag}</span>{" "}
                  <span className="vb-comp-quality-source">
                    ({f.computed.how}; read from {f.computed.source_fields.join(" and ")})
                  </span>
                </span>
              )}{" "}
              <span className="vb-comp-quality-source">Read from: {f.source_fields.join(", ")}</span>
            </li>
          ))}
        </ul>
      )}

      {deltas.length > 0 && (
        <p className="vb-comp-deltas">
          {/* Said out loud, because these three phrases are the only numbers on the card that are
              not on any record: they are this screen subtracting the subject from the sale. */}
          <span className="vb-comp-deltas-label">Against your subject</span>
          {deltas.map((d) => (
            <span className="vb-comp-delta" data-sense={d.sense} key={d.text}>{d.text}</span>
          ))}
          <span className="vb-comp-deltas-tag">computed</span>
        </p>
      )}

      {blocked && (
        <div className="vb-comp-blocked" role="group" aria-label={`What ${name} is waiting on`}>
          <p className="vb-comp-blocked-say">
            {/* The same sentence the table's row note carries, and the same rule about whose fault
                it is: a public-record candidate has no file to be short of anything. */}
            Needs {missing.map(label).join(" and ")} —{" "}
            {provider ? "the public record doesn't carry" : "your file doesn't state"}{" "}
            {missing.length === 1 ? "it" : "them"}. Enter{" "}
            {missing.length === 1 ? "it" : "them"} to include this sale.
          </p>
          {/* THE ONE PLACE A CARD EDITS. Everything else is corrected in the table, on purpose —
              but a sale that cannot be used until somebody types a number should not send that
              person to a different view to type it. */}
          <div className="vb-comp-blocked-fields">
            {missing.map((k) => (
              <label className="vb-comp-fix" key={k}>
                <span>{label(k)}</span>
                <CompNumInput
                  value={(comp[k as keyof CandidateComp] as number | null) ?? null}
                  edited={comp._raw != null && k in comp._raw}
                  needed
                  format={k === "price" ? "money" : k === "gla" || k === "lot_sf" ? "grouped" : undefined}
                  onChange={(v) => onEdit(index, { [k]: v } as Partial<CandidateComp>)}
                />
              </label>
            ))}
          </div>
        </div>
      )}

      {onLead && (
        selected && !blocked ? (
          <>
          {onStep && place !== null && (
            <CompStep name={name} index={index} place={place} onStep={onStep} onMove={onMove} placeCount={placeCount} upWhy={upWhy} downWhy={downWhy} />
          )}
          <label className="vb-comp-rank">
            <span>Order in the report</span>
            <select
              className="vb-comp-lead"
              value={leadOf(comp) ?? ""}
              aria-label={`Order of ${name} in the report`}
              onChange={(e) => onLead(index, e.target.value === "" ? null : (Number(e.target.value) as LeadPosition))}
            >
              <option value="">{leadOf(comp) === null && place !== null ? `Auto (${LEAD_SHORT[place]})` : "Auto"}</option>
              {reachable.map((pos) => <option key={pos} value={pos}>{LEAD_SHORT[pos]}</option>)}
            </select>
          </label>
          </>
        ) : (
          <p className="vb-comp-rank-why">{blocked ? "Fill in what this sale needs, then include it to rank it." : "Include this sale to rank it."}</p>
        )
      )}

      <div className="vb-comp-foot">
        <button
          type="button"
          className="vb-comp-more"
          aria-expanded={openFields}
          onClick={() => setOpenFields((v) => !v)}
        >
          {openFields ? "Hide the rest" : "Everything on record"}
          <span aria-hidden="true">{openFields ? "▴" : "▾"}</span>
        </button>
        <button type="button" className="vb-comp-more" onClick={onSwitchToTable}>
          Correct a figure
        </button>
      </div>

      {openFields && (
        <dl className="vb-comp-all">
          {ALL_FIELDS.map(({ key, label: l, render }) => {
            const shown = render(comp);
            return (
              <div className="vb-comp-all-row" key={key}>
                <dt>{l}</dt>
                <dd>{shown ?? "—"}</dd>
                {/* Every row keeps its own source. A card that showed a value without one would be
                    the exact thing this view is most at risk of becoming. */}
                <dd className="vb-comp-all-src">
                  {shown == null ? "Nothing on record" : sourceOf(comp, key)}
                </dd>
              </div>
            );
          })}
        </dl>
      )}
    </article>
  );
}

/**
 * THE COMP NUMBER, WITH A STEP EITHER SIDE (2026-11-03, owner). "Comp 2" with an up and a down
 * button: up moves it to 1 and the sale that held 1 becomes 2; down is the mirror. The number is
 * the sale's place in the report -- the same `_lead` mark the select below writes -- so the report
 * order and the map's pin numbers follow with no second piece of state.
 *
 * A BUTTON THAT CANNOT ACT SAYS WHY, IN THE SAME PLACE. At the ends the button is `aria-disabled`
 * rather than `disabled`: a truly disabled button leaves the tab order and its reason with it, and
 * a keyboard user would never learn why the arrow went dead. It stays focusable, does nothing when
 * pressed, carries the reason as its accessible name, and the reason is printed beside the number.
 */
function CompStep({ name, index, place, onStep, onMove, placeCount, upWhy, downWhy }: {
  onMove?: (index: number, place: number) => void;
  placeCount: number;
  name: string;
  index: number;
  place: LeadPosition;
  onStep: (index: number, direction: -1 | 1) => void;
  upWhy: string | null;
  downWhy: string | null;
}): ReactElement {
  const whyId = `vb-comp-step-why-${index}`;
  const menuId = `vb-comp-step-menu-${index}`;
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  const numBtn = useRef<HTMLButtonElement>(null);
  const canMove = !!onMove && placeCount > 1;
  const places = Array.from({ length: placeCount }, (_, k) => k + 1);
  const items = () => [...(wrap.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])') ?? [])];
  const close = (returnFocus: boolean) => { setOpen(false); if (returnFocus) numBtn.current?.focus(); };
  useEffect(() => {
    if (!open) return;
    items()[0]?.focus();
    const away = (e: MouseEvent) => { if (!wrap.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const onMenuKey = (e: React.KeyboardEvent) => {
    const list = items();
    const at = list.indexOf(document.activeElement as HTMLElement);
    if (e.key === "Escape") { e.preventDefault(); close(true); }
    else if (e.key === "Tab") setOpen(false);
    else if (e.key === "ArrowDown") { e.preventDefault(); list[(at + 1) % list.length]?.focus(); }
    else if (e.key === "ArrowUp") { e.preventDefault(); list[(at - 1 + list.length) % list.length]?.focus(); }
    else if (e.key === "Home") { e.preventDefault(); list[0]?.focus(); }
    else if (e.key === "End") { e.preventDefault(); list[list.length - 1]?.focus(); }
  };
  const numWhy = canMove ? null : (onMove ? upWhy ?? downWhy : null);
  const reasons = [...new Set([upWhy, downWhy].filter((r): r is string => !!r))];
  const button = (direction: -1 | 1, why: string | null) => {
    const to = place + direction;
    const word = direction < 0 ? "up" : "down";
    return (
      <button
        type="button"
        className="vb-comp-step-btn"
        data-step={word}
        aria-disabled={why ? "true" : undefined}
        aria-describedby={why ? whyId : undefined}
        aria-label={why ? `Move comp ${place} ${word}: ${why}` : `Move comp ${place} ${word} to ${to}`}
        title={why ?? `Move comp ${place} ${word} to ${to}`}
        onClick={why ? undefined : () => onStep(index, direction)}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
          <path d={direction < 0 ? "M3.5 10.5L8 6l4.5 4.5" : "M3.5 5.5L8 10l4.5-4.5"} stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    );
  };
  return (
    <div className="vb-comp-step" role="group" aria-label={`Place of ${name} in the report`}>
      {button(-1, upWhy)}
      <span className="vb-comp-step-num" data-testid="comp-step-num" ref={wrap}>
        {onMove ? (
          <button
            type="button"
            ref={numBtn}
            className="vb-comp-step-numbtn"
            data-step="num"
            aria-haspopup="menu"
            aria-expanded={open}
            aria-controls={open ? menuId : undefined}
            aria-disabled={canMove ? undefined : "true"}
            aria-describedby={canMove ? undefined : whyId}
            aria-label={canMove ? `Comp ${place}: move to another place` : `Comp ${place}: ${numWhy ?? "nothing to reorder"}`}
            title={canMove ? "Move to another place" : numWhy ?? undefined}
            onClick={canMove ? () => setOpen((v) => !v) : undefined}
            onKeyDown={canMove ? (e) => { if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); } } : undefined}
          >
            <span className="vb-comp-step-word">Comp </span>{place}
            <svg width="10" height="10" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
              <path d="M3.5 6l4.5 4.5L12.5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        ) : (
          <><span className="vb-comp-step-word">Comp </span>{place}</>
        )}
        {open && (
          <ul id={menuId} role="menu" className="vb-comp-step-menu" aria-label={`Move ${name} to place`} onKeyDown={onMenuKey}>
            {places.map((p) => {
              const current = p === place;
              return (
                <li role="none" key={p}>
                  <button
                    type="button"
                    role="menuitem"
                    tabIndex={-1}
                    className="vb-comp-step-item"
                    aria-disabled={current ? "true" : undefined}
                    aria-current={current ? "true" : undefined}
                    onClick={current ? undefined : () => { setOpen(false); onMove?.(index, p); }}
                  >
                    {`Move to place ${p}${current ? " (current)" : ""}`}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </span>
      {button(1, downWhy)}
      {reasons.length > 0 && <span className="vb-comp-step-why" id={whyId}>{reasons.join(" ")}</span>}
    </div>
  );
}

/**
 * Everything the grid holds, read-only, in the expander.
 *
 * READ-ONLY IS THE POINT. Two editable spellings of the same eighteen fields is two places for an
 * edit to be lost and two places for the override trail to disagree with itself. The table edits;
 * this reads; the blocked-row fixer above is the single, deliberate exception.
 */
/** Card-width names for the three headline figures. The full names live on the `Field`. */
const SHORT_LABEL: Record<string, string> = {
  price: "Sold price",
  gla: "Living area",
  ppsf: "$ per SF",
};

/** R6: where a comp's lot figure comes from. The row's own source tag (the MLS number) for a stated
 * lot; "computed" with the arithmetic when it was converted from acres (and the square feet shown,
 * since no stated figure is); "Lot size not on record" when neither is stated. Null when there is
 * nothing to add (an uploaded or typed row that carries no per-field tag). */
export function lotProvenance(c: CandidateComp): string | null {
  if (c._lot_unstated) return "Lot size not on record";
  if (c._lot_computed_sf != null) {
    const from = c._lot_computed_from ? `, ${c._lot_computed_from}` : "";
    return `${sqft(c._lot_computed_sf)} lot, computed (${c._lot_computed_note ?? "ac x 43,560"})${from}`;
  }
  const tag = c._field_source_labels?.lot_sf;
  return tag && c.lot_sf != null ? tag : null;
}

const ALL_FIELDS: { key: string; label: string; render: (c: CandidateComp) => string | null }[] = [
  { key: "sale_date", label: "Sale date", render: (c) => c.sale_date ? shortDate(c.sale_date) : null },
  { key: "price", label: "Sold price", render: (c) => c.price != null ? money(c.price) : null },
  { key: "seller_concession", label: "Seller concession",
    render: (c) => c.seller_concession != null ? money(c.seller_concession) : null },
  { key: "gla", label: "Living area", render: (c) => c.gla != null ? sqft(c.gla) : null },
  { key: "lot_sf", label: "Lot size", render: (c) => c.lot_sf != null ? sqft(c.lot_sf) : null },
  { key: "year", label: "Year built", render: (c) => c.year != null ? String(c.year) : null },
  { key: "beds", label: "Bedrooms", render: (c) => c.beds != null ? String(c.beds) : null },
  { key: "full_baths", label: "Full baths", render: (c) => c.full_baths != null ? String(c.full_baths) : null },
  { key: "half_baths", label: "Half baths", render: (c) => c.half_baths != null ? String(c.half_baths) : null },
  { key: "garage", label: "Garage bays", render: (c) => c.garage != null ? String(c.garage) : null },
  // A boolean the wire cannot express as "not stated" — see COMP_COLUMNS' note on `pool`. Shown as
  // the flag's own two states and never as an absence, because an absence is not what it holds.
  { key: "pool", label: "Pool", render: (c) => (c.pool ? "Yes" : "No") },
  { key: "subdivision", label: "Neighborhood", render: (c) => c.subdivision ?? null },
  { key: "distance", label: "Distance", render: (c) =>
      typeof c.distance === "number" && Number.isFinite(c.distance) ? `${c.distance.toFixed(2)} mi` : null },
  { key: "dom", label: "Days on market",
    render: (c) => { const l = daysOnMarketLabel(c, c.dom_as_of); return l === "—" ? null : l; } },
  { key: "condition", label: "Condition", render: (c) => c.condition || null },
  { key: "sale_type", label: "Sale type", render: (c) => c.sale_type || null },
  { key: "mls_id", label: "MLS number", render: (c) => c.mls_id ?? null },
];

/** The colour tokens this file's stylesheet mirrors, kept beside it so a token change is one grep. */
export const COMP_CARD_TOKENS = {
  border: colors.border,
  accent: colors.accent,
  caution: colors.caution,
} as const;
